import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';
import { settingsService } from '../services/api';
import CaptureAttemptsDialog from '../components/settings/CaptureAttemptsDialog';

// Screenshot / screen-recording blocking is deliberately NOT listed here. It is
// always on and enforced natively in the mobile app, because a remotely
// toggleable flag would fail open and silently drop NDA protection.
const TOGGLES = [
  { key: 'enableAppleComplianceMode', label: 'appleComplianceMode' },
  { key: 'enableCaptureReporting', label: 'captureReporting' },
  { key: 'enablePdfSharing', label: 'pdfSharing' }
];

const Settings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [attemptsOpen, setAttemptsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsService.getAppSettings();
      setSettings(response.settings ?? {});
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, event) => {
    const newValue = event.target.checked;
    setSavingKey(key);
    try {
      await settingsService.updateAppSettings({ [key]: newValue });
      setSettings((prev) => ({ ...prev, [key]: newValue }));
      setSnackbar({
        open: true,
        message: t('settings.updateSuccess'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      setSnackbar({
        open: true,
        message: t('settings.updateFailed'),
        severity: 'error'
      });
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
          {t('settings.title')}
        </Typography>
      </Box>

      <Alert severity="info" icon={<SecurityIcon />} sx={{ mb: 3, borderRadius: 2 }}>
        <AlertTitle sx={{ fontWeight: 600 }}>{t('settings.captureBlockingAlwaysOn')}</AlertTitle>
        {t('settings.captureBlockingAlwaysOnDesc')}
      </Alert>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {TOGGLES.map(({ key, label }, index) => {
          const value = settings[key] ?? false;
          return (
            <React.Fragment key={key}>
              {index > 0 && <Divider sx={{ my: 3 }} />}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {t(`settings.${label}`)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    {t(`settings.${label}Desc`)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: value ? 'success.main' : 'text.secondary' }}
                  >
                    {value ? t('settings.enabled') : t('settings.disabled')}
                  </Typography>
                </Box>
                <Switch
                  checked={value}
                  onChange={(event) => handleToggle(key, event)}
                  disabled={savingKey !== null}
                  color="success"
                />
              </Box>
            </React.Fragment>
          );
        })}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => setAttemptsOpen(true)}>
          {t('settings.viewCaptureAttempts')}
        </Button>
      </Box>

      <CaptureAttemptsDialog open={attemptsOpen} onClose={() => setAttemptsOpen(false)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
